import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Maximize2, Minimize2, X, Layers, ChevronLeft, ChevronRight } from "lucide-react";
import { useSelfPosition } from "../hooks/useSelfPosition";
import { useTranslationQueue } from "../hooks/useTranslationQueue";
import { useTranslationWindowSettings } from "../hooks/useTranslationWindowSettings";
import { useFittedText } from "../hooks/useFittedText";
import { TranslationMessageBand } from "../components/TranslationMessageBand";
import { useTranslationWindowBridge } from "../hooks/useTranslationWindowBridge";

// Constants for auto-sizing - responsive values
const MAX_SLIDES = 2;
const SLIDE_DURATION = 5000; // 5 seconds per slide

// Get responsive font sizes based on screen width
const getResponsiveFontSizes = () => {
  const width = typeof window !== 'undefined' ? window.innerWidth : 1000;
  
  if (width < 480) {
    // Small mobile
    return { base: 28, min: 16, max: 48 };
  } else if (width < 768) {
    // Mobile/tablet
    return { base: 36, min: 18, max: 56 };
  } else if (width < 1024) {
    // Tablet/small desktop
    return { base: 52, min: 24, max: 72 };
  } else {
    // Desktop
    return { base: 72, min: 28, max: 96 };
  }
};

// Calculate optimal font size to fit text in max 2 slides (responsive)
// The middle region always occupies 70%, independent of message visibility.
const calculateOptimalFontSize = (text) => {
  if (!text || text === "..." || text === "Aguardando tradução...") {
    return getResponsiveFontSizes().base;
  }

  const { base: BASE_FONT_SIZE, min: MIN_FONT_SIZE, max: MAX_FONT_SIZE } = getResponsiveFontSizes();
  const containerWidth = typeof window !== 'undefined' ? window.innerWidth * 0.9 : 1000;
  
  const containerHeight = typeof window !== 'undefined'
    ? Math.max(1, window.innerHeight * 0.7 - 48)
    : 500;

  const words = text.split(' ');
  const totalWords = words.length;
  
  // Start with base font size and reduce if needed
  let fontSize = Math.min(BASE_FONT_SIZE, MAX_FONT_SIZE);
  
  // Estimate how many words can fit in the container
  const getWordsPerSlide = (size) => {
    // Estimate characters per line based on font size - more conservative for mobile
    const charWidthFactor = containerWidth < 500 ? 0.65 : 0.55;
    const charsPerLine = Math.floor(containerWidth / (size * charWidthFactor));
    const avgWordLength = 6;
    const wordsPerLine = Math.max(2, Math.floor(charsPerLine / avgWordLength));
    
    // Estimate lines that fit in container (with some padding)
    const lineHeight = size * 1.4;
    const maxLines = Math.floor(containerHeight / lineHeight);
    
    return wordsPerLine * Math.max(2, maxLines);
  };
  
  // Find the minimum font size that fits in MAX_SLIDES
  while (fontSize > MIN_FONT_SIZE) {
    const wordsPerSlide = getWordsPerSlide(fontSize);
    const requiredSlides = Math.ceil(totalWords / wordsPerSlide);
    
    if (requiredSlides <= MAX_SLIDES) {
      break;
    }
    
    fontSize -= 2; // Decrease by 2px increments for finer control
  }
  
  return fontSize;
};

// Function to split text into max 2 slides based on word count
const splitTextIntoSlides = (text, fontSize) => {
  if (!text || text === "..." || text === "Aguardando tradução...") {
    return [text || "Aguardando tradução..."];
  }

  const words = text.split(' ');
  const totalWords = words.length;
  
  // Calculate approximate words per slide based on font size
  // Larger font = fewer words per slide
  const wordsPerSlide = fontSize > 60 ? 25 : fontSize > 45 ? 40 : 60;
  
  // If text fits in one slide, return it as is
  if (totalWords <= wordsPerSlide) {
    return [text];
  }
  
  // Otherwise, split into exactly 2 slides (balanced)
  const midpoint = Math.ceil(totalWords / 2);
  const slide1 = words.slice(0, midpoint).join(' ');
  const slide2 = words.slice(midpoint).join(' ');
  
  return [slide1, slide2];
};

// Custom hook for slide management with auto-sizing
const useSlideManager = (translationText, autoAdvance) => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [calculatedFontSize, setCalculatedFontSize] = useState(() => getResponsiveFontSizes().base);
  const slideTimerRef = useRef(null);
  const currentSlideRef = useRef(0);
  const slidesLengthRef = useRef(1);

  // Calculate optimal font size when text or window size changes
  useEffect(() => {
    const calculateSize = () => {
      const optimalSize = calculateOptimalFontSize(translationText);
      setCalculatedFontSize(optimalSize);
    };
    
    calculateSize();
    
    // Recalculate on window resize
    window.addEventListener('resize', calculateSize);
    return () => window.removeEventListener('resize', calculateSize);
  }, [translationText]);

  // Split translation into max 2 slides
  const slides = useMemo(() => {
    const result = splitTextIntoSlides(translationText, calculatedFontSize);
    slidesLengthRef.current = result.length;
    return result;
  }, [translationText, calculatedFontSize]);

  // Advance slide function
  const advanceSlide = useCallback(() => {
    if (currentSlideRef.current < slidesLengthRef.current - 1) {
      const nextIndex = currentSlideRef.current + 1;
      setCurrentSlideIndex(nextIndex);
      currentSlideRef.current = nextIndex;
    }
  }, []);

  // Reset and start slideshow when translation changes
  useEffect(() => {
    if (slideTimerRef.current) {
      clearTimeout(slideTimerRef.current);
      slideTimerRef.current = null;
    }
    
    setCurrentSlideIndex(0);
    currentSlideRef.current = 0;
    
    // Auto-advance to slide 2 if there are 2 slides
    if (autoAdvance && slides.length > 1) {
      slideTimerRef.current = setTimeout(advanceSlide, SLIDE_DURATION);
    }
    
    return () => {
      if (slideTimerRef.current) {
        clearTimeout(slideTimerRef.current);
      }
    };
  }, [translationText, autoAdvance, slides.length, advanceSlide]);

  // Keep current slide ref in sync
  useEffect(() => {
    currentSlideRef.current = currentSlideIndex;
  }, [currentSlideIndex]);

  const goToNextSlide = useCallback(() => {
    setCurrentSlideIndex(prev => Math.min(prev + 1, slides.length - 1));
  }, [slides.length]);

  const goToPrevSlide = useCallback(() => {
    setCurrentSlideIndex(prev => Math.max(prev - 1, 0));
  }, []);

  const goToSlide = useCallback((index) => {
    setCurrentSlideIndex(index);
  }, []);

  return {
    slides,
    currentSlideIndex,
    goToNextSlide,
    goToPrevSlide,
    goToSlide,
    hasMultipleSlides: slides.length > 1,
    currentSlide: slides[currentSlideIndex] || "Aguardando tradução...",
    calculatedFontSize
  };
};

// Custom hook for overlay mode
const useOverlayMode = (overlayMode) => {
  const focusIntervalRef = useRef(null);

  useEffect(() => {
    if (!overlayMode) {
      if (focusIntervalRef.current) {
        clearInterval(focusIntervalRef.current);
        focusIntervalRef.current = null;
      }
      return;
    }

    const bringToFront = () => {
      try {
        window.focus();
        if (document.hasFocus && !document.hasFocus()) {
          window.blur();
          window.focus();
        }
      } catch (error) {
        // Focus may fail in some browser contexts (e.g., iframe restrictions)
        console.debug("Focus operation failed:", error.message);
      }
    };

    bringToFront();
    focusIntervalRef.current = setInterval(bringToFront, 2000);

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        bringToFront();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      if (focusIntervalRef.current) {
        clearInterval(focusIntervalRef.current);
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [overlayMode]);
};

// Custom hook for controls visibility
const useControlsVisibility = () => {
  const [showControls, setShowControls] = useState(false);
  const timeoutRef = useRef(null);

  const handleMouseMove = useCallback(() => {
    setShowControls(true);
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, 2000);
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return { showControls, handleMouseMove };
};

// Custom hook for fullscreen
const useFullscreen = () => {
  const [isFullscreen, setIsFullscreen] = useState(false);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
      setIsFullscreen(true);
    } else {
      document.exitFullscreen().catch(() => {});
      setIsFullscreen(false);
    }
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  return { isFullscreen, toggleFullscreen };
};

export default function TranslationWindow() {
  const [searchParams] = useSearchParams();
  useSelfPosition(searchParams);
  const { snapshot: presenterSnapshot, activeRef: presenterActiveRef } = useTranslationWindowBridge(1);

  // Subtitle-style display queue: buffers rapid updates, holds each translation
  // for a minimum readable time based on text length (CPS-based).
  const initialText = localStorage.getItem("currentTranslation") || "";
  const { displayText: translationText, enqueueTranslation } =
    useTranslationQueue(initialText, presenterSnapshot?.readingSpeed);

  const [lastTimestamp, setLastTimestamp] = useState(() => {
    const stored = localStorage.getItem("translationTimestamp");
    return stored ? parseInt(stored, 10) : 0;
  });
  const { headerSettings, footerSettings, overlayMode, toggleOverlayMode } = useTranslationWindowSettings("translationWindowSettings", presenterSnapshot?.settings);
  const lastPresenterCaptionRef = useRef(null);
  useEffect(() => {
    const caption = presenterSnapshot?.translation;
    if (!caption) return;
    const key = JSON.stringify(caption);
    if (lastPresenterCaptionRef.current === key) return;
    lastPresenterCaptionRef.current = key;
    if (caption.text && caption.text !== "Aguardando tradução..." && caption.text.trim() !== "...") {
      enqueueTranslation(caption.text);
      setLastTimestamp(caption.timestamp);
    }
  }, [presenterSnapshot, enqueueTranslation]);
  
  // OBS Mode parameters from URL
  const obsMode = searchParams.get("mode") === "obs";
  const obsBg = searchParams.get("bg") || "transparent"; // transparent, green, blue, black
  const obsFontSize = parseInt(searchParams.get("fontSize") || "48", 10);
  const obsColorParam = searchParams.get("color") || "ffffff";
  // Support both hex (without #) and named colors
  const obsColor = obsColorParam.match(/^[0-9a-fA-F]{6}$/) ? `#${obsColorParam}` : obsColorParam;
  const obsPosition = searchParams.get("position") || "bottom"; // top, center, bottom
  const obsShadow = searchParams.get("shadow") !== "false"; // default true
  const obsPadding = parseInt(searchParams.get("padding") || "20", 10);
  const obsMaxWidth = parseInt(searchParams.get("maxWidth") || "90", 10); // percentage
  const obsAnimation = searchParams.get("animation") !== "false"; // default true
  
  // Custom hooks
  const { showControls, handleMouseMove } = useControlsVisibility();
  const { isFullscreen, toggleFullscreen } = useFullscreen();
  useOverlayMode(overlayMode);
  
  const {
    slides,
    currentSlideIndex,
    goToNextSlide,
    goToPrevSlide,
    goToSlide,
    hasMultipleSlides,
    currentSlide,
    calculatedFontSize
  } = useSlideManager(translationText, true);
  const { containerRef: textContainerRef, textRef } = useFittedText(currentSlide, calculatedFontSize, !obsMode);

  // Get initial text from URL params
  useEffect(() => {
    const text = searchParams.get("text");
    // Guard: Dashboard passes 'Aguardando tradução...' as placeholder when
    // no translation exists yet. That placeholder must NEVER enter the queue
    // (it would just delay real translations by the min-hold time).
    if (text && text !== "Aguardando tradução..." && text.trim() !== "...") {
      enqueueTranslation(text);
    }
  }, [searchParams, enqueueTranslation]);

  // Poll localStorage for translation updates
  useEffect(() => {
    const pollInterval = setInterval(() => {
      if (presenterActiveRef.current) return;
      const storedTranslation = localStorage.getItem("currentTranslation");
      const storedTimestamp = localStorage.getItem("translationTimestamp");

      if (storedTimestamp && storedTranslation) {
        const timestamp = parseInt(storedTimestamp, 10);
        // Only enqueue newer translations (based on timestamp)
        if (timestamp > lastTimestamp) {
          enqueueTranslation(storedTranslation);
          setLastTimestamp(timestamp);

          if (overlayMode) {
            try {
              window.focus();
            } catch (error) {
              console.debug("Focus operation failed:", error.message);
            }
          }
        }
      }
    }, 300);

    return () => clearInterval(pollInterval);
  }, [lastTimestamp, overlayMode, enqueueTranslation, presenterActiveRef]);

  // Listen for storage events
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (presenterActiveRef.current) return;
      if (event.key === "currentTranslation" && event.newValue) {
        enqueueTranslation(event.newValue);
        const timestamp = localStorage.getItem("translationTimestamp");
        if (timestamp) {
          setLastTimestamp(parseInt(timestamp, 10));
        }

        if (overlayMode) {
          try {
            window.focus();
          } catch (error) {
            console.debug("Focus operation failed:", error.message);
          }
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [overlayMode, enqueueTranslation, presenterActiveRef]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        goToNextSlide();
      } else if (e.key === 'ArrowLeft') {
        goToPrevSlide();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToNextSlide, goToPrevSlide]);

  const handleClose = useCallback(() => {
    window.close();
  }, []);

  // OBS Mode - Simplified overlay for streaming
  if (obsMode) {
    const getBgColor = () => {
      switch (obsBg) {
        case "green": return "#00FF00";
        case "blue": return "#0000FF";
        case "magenta": return "#FF00FF";
        case "black": return "#000000";
        case "transparent":
        default: return "transparent";
      }
    };

    const getPositionStyles = () => {
      switch (obsPosition) {
        case "top":
          return { top: `${obsPadding}px`, left: "50%", transform: "translateX(-50%)" };
        case "center":
          return { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
        case "bottom":
        default:
          return { bottom: `${obsPadding}px`, left: "50%", transform: "translateX(-50%)" };
      }
    };

    const textShadow = obsShadow 
      ? "2px 2px 4px rgba(0,0,0,0.8), -1px -1px 2px rgba(0,0,0,0.5), 0 0 10px rgba(0,0,0,0.5)" 
      : "none";

    return (
      <div 
        style={{ 
          position: "fixed",
          inset: 0,
          backgroundColor: getBgColor(),
          overflow: "hidden"
        }}
        data-testid="translation-window-obs"
      >
        <div
          style={{
            position: "absolute",
            ...getPositionStyles(),
            width: `${obsMaxWidth}%`,
            maxWidth: `${obsMaxWidth}%`,
            textAlign: "center",
            transition: obsAnimation ? "all 0.3s ease-out" : "none"
          }}
        >
          <p
            style={{
              color: obsColor,
              fontSize: `${obsFontSize}px`,
              fontWeight: "bold",
              fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              lineHeight: 1.3,
              textShadow: textShadow,
              margin: 0,
              padding: `${obsPadding}px`,
              wordWrap: "break-word",
              overflowWrap: "break-word"
            }}
          >
            {currentSlide || "Aguardando tradução..."}
          </p>
        </div>

        {/* Slide indicators - subtle */}
        {hasMultipleSlides && (
          <div 
            style={{
              position: "absolute",
              bottom: "5px",
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              gap: "4px"
            }}
          >
            {slides.map((slide, index) => (
              <div
                key={`obs-slide-${index}-${slide?.substring(0, 10) || index}`}
                style={{
                  width: "6px",
                  height: "6px",
                  borderRadius: "50%",
                  backgroundColor: index === currentSlideIndex ? "white" : "rgba(255,255,255,0.3)",
                  transition: "background-color 0.3s"
                }}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div 
      className="fixed inset-0 z-50 grid bg-black overflow-hidden"
      style={{ gridTemplateRows: "15% 70% 15%", gridTemplateColumns: "minmax(0, 1fr)" }}
      onMouseMove={handleMouseMove}
      data-testid="translation-window"
      data-sync-source={presenterSnapshot ? "presenter" : "storage"}
    >
      {/* Controls overlay */}
      <div 
        className={`fixed top-0 right-0 p-4 flex gap-2 z-60 transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0'}`}
      >
        <button
          onClick={toggleOverlayMode}
          className={`p-2 rounded-lg transition-colors ${
            overlayMode 
              ? 'bg-blue-600 hover:bg-blue-700 text-white' 
              : 'bg-zinc-800/80 hover:bg-zinc-700 text-white'
          }`}
          data-testid="overlay-button"
          aria-label={overlayMode ? "Desativar sobreposição" : "Ativar sobreposição"}
          title={overlayMode ? "Sobreposição: LIGADA" : "Sobreposição: DESLIGADA"}
        >
          <Layers className="w-5 h-5" />
        </button>
        
        <button
          onClick={toggleFullscreen}
          className="p-2 rounded-lg bg-zinc-800/80 hover:bg-zinc-700 text-white transition-colors"
          data-testid="fullscreen-button"
          aria-label={isFullscreen ? "Sair do modo tela cheia" : "Modo tela cheia"}
        >
          {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
        </button>
        
        <button
          onClick={handleClose}
          className="p-2 rounded-lg bg-zinc-800/80 hover:bg-red-600 text-white transition-colors"
          data-testid="close-button"
          aria-label="Fechar janela"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Overlay mode indicator */}
      {overlayMode && (
        <div 
          className={`fixed top-4 left-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-600 text-white text-xs z-60 transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-30'}`}
        >
          <Layers className="w-3 h-3" />
          <span className="hidden sm:inline">Sobreposição Ativa</span>
        </div>
      )}

      <TranslationMessageBand kind="header" settings={headerSettings} />

      {/* Translation text - constrained to available space */}
      <div className="flex items-center justify-center px-4 sm:px-8 md:px-12 relative overflow-hidden min-h-0 min-w-0" data-testid="translation-content-container">
        {/* Previous slide button */}
        {hasMultipleSlides && currentSlideIndex > 0 && (
          <button
            onClick={goToPrevSlide}
            className={`absolute left-2 sm:left-4 p-2 sm:p-3 rounded-full bg-zinc-800/50 hover:bg-zinc-700/70 text-white transition-all duration-300 z-10 ${showControls ? 'opacity-100' : 'opacity-0'}`}
            data-testid="prev-slide-button"
            aria-label="Slide anterior"
          >
            <ChevronLeft className="w-6 h-6 sm:w-8 sm:h-8" />
          </button>
        )}

        <div ref={textContainerRef} className="w-full max-w-5xl h-full mx-auto flex items-center justify-center text-center px-2 sm:px-4 py-6 min-h-0 min-w-0">
          <p 
            ref={textRef}
            className="w-full text-white font-bold leading-snug break-words"
            style={{
              fontSize: `${calculatedFontSize}px`,
              animation: 'fadeIn 0.3s ease-in-out',
              wordWrap: 'break-word',
              overflowWrap: 'anywhere',
            }}
            data-testid="translation-display-text"
          >
            {currentSlide}
          </p>

          {/* Slide indicator - only show when there are 2 slides */}
          {hasMultipleSlides && (
            <div className={`absolute bottom-2 left-0 right-0 flex items-center justify-center gap-2 transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-50'}`}>
              {slides.map((_, index) => (
                <button
                  key={`slide-${index}-${slides.length}`}
                  onClick={() => goToSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    index === currentSlideIndex 
                      ? 'bg-white scale-125' 
                      : 'bg-white/30 hover:bg-white/50'
                  }`}
                  aria-label={`Ir para slide ${index + 1}`}
                  data-testid={`slide-${index + 1}-button`}
                />
              ))}
              <span className="ml-4 text-white/50 text-sm" data-testid="slide-counter">
                {currentSlideIndex + 1} / {slides.length}
              </span>
            </div>
          )}
        </div>

        {/* Next slide button */}
        {hasMultipleSlides && currentSlideIndex < slides.length - 1 && (
          <button
            onClick={goToNextSlide}
            className={`absolute right-2 sm:right-4 p-2 sm:p-3 rounded-full bg-zinc-800/50 hover:bg-zinc-700/70 text-white transition-all duration-300 z-10 ${showControls ? 'opacity-100' : 'opacity-0'}`}
            data-testid="next-slide-button"
            aria-label="Próximo slide"
          >
            <ChevronRight className="w-6 h-6 sm:w-8 sm:h-8" />
          </button>
        )}
      </div>

      <TranslationMessageBand kind="footer" settings={footerSettings} />

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
